import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface FormData {
  caregiver_name: string;
  caregiver_phone: string;
  caregiver_birth: string;
  caregiver_ssn: string;
  caregiver_gender: string;
  caregiver_address: string;
  patient_name: string;
  patient_phone: string;
  patient_birth: string;
  patient_gender: string;
  patient_address: string;
  insurance_company: string;
  agent_phone: string;
}

const initialForm: FormData = {
  caregiver_name: '',
  caregiver_phone: '',
  caregiver_birth: '',
  caregiver_ssn: '',
  caregiver_gender: '',
  caregiver_address: '',
  patient_name: '',
  patient_phone: '',
  patient_birth: '',
  patient_gender: '',
  patient_address: '',
  insurance_company: '',
  agent_phone: '',
};

function generateReceiptNumber(): string {
  const now = new Date();
  const y = now.getFullYear().toString().slice(2);
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const rand = String(Math.floor(Math.random() * 9000) + 1000);
  return `RC${y}${m}${d}-${rand}`;
}

const INPUT_CLASS =
  'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] transition-colors bg-white placeholder-gray-300';
const LABEL_CLASS = 'block text-xs font-semibold text-gray-600 mb-1.5';

interface Props {
  onSuccess: (receiptNumber: string) => void;
}

export default function CareRequestForm({ onSuccess }: Props) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssnFront, setSsnFront] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 필수 필드 검증
    const required: (keyof FormData)[] = [
      'caregiver_name', 'caregiver_phone', 'caregiver_birth', 'caregiver_ssn', 'caregiver_gender', 'caregiver_address',
      'patient_name', 'patient_phone', 'patient_birth', 'patient_gender', 'patient_address',
    ];
    for (const field of required) {
      if (!form[field].trim()) {
        setError('필수 항목을 모두 입력해주세요.');
        return;
      }
    }

    setLoading(true);
    const now = new Date();
    const receiptNumber = generateReceiptNumber();

    const payload = {
      receipt_time: now.toISOString(),
      receipt_number: receiptNumber,
      caregiver_name: form.caregiver_name,
      caregiver_phone: form.caregiver_phone,
      caregiver_birth: form.caregiver_birth,
      caregiver_ssn: form.caregiver_ssn,
      caregiver_gender: form.caregiver_gender,
      caregiver_address: form.caregiver_address,
      patient_name: form.patient_name,
      patient_phone: form.patient_phone,
      patient_birth: form.patient_birth,
      patient_gender: form.patient_gender,
      patient_address: form.patient_address,
      insurance_company: form.insurance_company || null,
      agent_phone: form.agent_phone || null,
      status: '접수',
    };

    const { error: dbError } = await supabase.from('care_requests').insert([payload]);

    if (dbError) {
      setLoading(false);
      setError(`저장 중 오류가 발생했습니다: ${dbError.message}`);
      return;
    }

    // 문자 알림 발송 (비동기 - 실패해도 폼 제출은 성공 처리)
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      await fetch(`${supabaseUrl}/functions/v1/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          receipt_number: payload.receipt_number,
          receipt_time: payload.receipt_time,
          caregiver_name: payload.caregiver_name,
          caregiver_phone: payload.caregiver_phone,
          patient_name: payload.patient_name,
          patient_phone: payload.patient_phone,
          insurance_company: payload.insurance_company,
        }),
      });
    } catch {
      // 알림 실패는 무시 (DB 저장은 이미 성공)
    }

    setLoading(false);
    onSuccess(receiptNumber);
    setForm(initialForm);
    setSsnFront('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 간병인 정보 */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#e8f5f1' }}>
            <i className="ri-nurse-line text-sm" style={{ color: '#2E7D6B' }}></i>
          </div>
          <h3 className="text-base font-bold text-gray-900">간병인 정보</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>간병인 성명 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="caregiver_name"
              value={form.caregiver_name}
              onChange={handleChange}
              placeholder="홍길동"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>간병인 연락처 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="caregiver_phone"
              value={form.caregiver_phone}
              onChange={handleChange}
              placeholder="010-0000-0000"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>간병인 생년월일 (6자리) <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="caregiver_birth"
              value={form.caregiver_birth}
              onChange={handleChange}
              placeholder="850101"
              maxLength={6}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>간병인 주민등록번호 (앞자리 6자리) <span className="text-red-400">*</span></label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={ssnFront}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setSsnFront(val);
                setForm((prev) => ({ ...prev, caregiver_ssn: val }));
              }}
              placeholder="예: 850101"
              className={`${INPUT_CLASS} text-center tracking-widest`}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>간병인 성별 <span className="text-red-400">*</span></label>
            <select
              name="caregiver_gender"
              value={form.caregiver_gender}
              onChange={handleChange}
              className={INPUT_CLASS}
            >
              <option value="">선택하세요</option>
              <option value="남">남</option>
              <option value="여">여</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL_CLASS}>간병인 주소 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="caregiver_address"
              value={form.caregiver_address}
              onChange={handleChange}
              placeholder="예) 서울특별시 강남구 테헤란로 123"
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>

      {/* 환자 정보 */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#fff4e8' }}>
            <i className="ri-heart-pulse-line text-sm" style={{ color: '#e07b39' }}></i>
          </div>
          <h3 className="text-base font-bold text-gray-900">환자 정보</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>환자 성명 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="patient_name"
              value={form.patient_name}
              onChange={handleChange}
              placeholder="홍길동"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>환자 연락처 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="patient_phone"
              value={form.patient_phone}
              onChange={handleChange}
              placeholder="010-0000-0000"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>환자 생년월일 (6자리) <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="patient_birth"
              value={form.patient_birth}
              onChange={handleChange}
              placeholder="500101"
              maxLength={6}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>환자 성별 <span className="text-red-400">*</span></label>
            <select
              name="patient_gender"
              value={form.patient_gender}
              onChange={handleChange}
              className={INPUT_CLASS}
            >
              <option value="">선택하세요</option>
              <option value="남">남</option>
              <option value="여">여</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL_CLASS}>환자 주소 <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="patient_address"
              value={form.patient_address}
              onChange={handleChange}
              placeholder="예) 서울특별시 강남구 테헤란로 123"
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>

      {/* 보험 정보 */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#eef2ff' }}>
            <i className="ri-shield-check-line text-sm" style={{ color: '#5c6bc0' }}></i>
          </div>
          <h3 className="text-base font-bold text-gray-900">보험 정보 <span className="text-xs font-normal text-gray-400 ml-1">(선택)</span></h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>가입보험사</label>
            <input
              type="text"
              name="insurance_company"
              value={form.insurance_company}
              onChange={handleChange}
              placeholder="삼성생명, 한화생명 등"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>설계사 연락처</label>
            <input
              type="text"
              name="agent_phone"
              value={form.agent_phone}
              onChange={handleChange}
              placeholder="010-0000-0000"
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl">
          <i className="ri-error-warning-line text-red-400 text-sm"></i>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#2E7D6B' }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>저장 중...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-save-line"></i>신청 접수 저장
          </span>
        )}
      </button>
    </form>
  );
}
